import type { CommonSort, NestedFilter } from '@@partner:types:graphql'

import React, { useCallback, useState } from 'react'
import { useFetchCategories } from '@@partner:graphql/query/catalog/categories.gql'
import { generatePath } from 'react-router-dom'

import { Pagination } from 'rsuite'
import CategoriesTable from './grid/CategoriesTable'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'

import { SORT_ORDER_DESC, SORT_ORDER_ASC } from '@@partner:config/graphql'
import { ROUTE_CATALOG_CATEGORY_EDIT } from '@@partner:config/routes'
import CategoriesFilter from './grid/CategoriesFilter'
import { convertToInt } from '@@partner:helpers/util'

export interface CategoryGridProps {
  brandSlug: string
}

export default function CategoryGrid ({ brandSlug }: CategoryGridProps) {
  const [ page, setPage ] = useState(1)
  const [ tableSort, setTableSort ] = useState<CommonSort>({
    fieldName: 'id',
    direction: SORT_ORDER_DESC
  })
  const [ filter, setFilter ] = useState<NestedFilter>({
    fields: [ 'id' ],
    op: 'unwrap'
  })

  const { data, loading, error } = useFetchCategories({
    pager: { page },
    sort: tableSort,
    filter
  })

  const generateEditUrl = useCallback(
    (categoryId) => {
      if (!brandSlug) {
        return ''
      }
      return generatePath(ROUTE_CATALOG_CATEGORY_EDIT, {
        brandSlug,
        categoryId
      })
    },
    [ brandSlug ]
  )

  const handlePageSelect = useCallback((selectedPage: string | number) => {
    setPage(convertToInt(selectedPage))
  }, [])

  const handleSortColumn = useCallback((fieldName, direction) => {
    setTableSort({
      fieldName,
      direction: direction === 'desc' ? SORT_ORDER_DESC : SORT_ORDER_ASC
    })
  }, [])

  const handleFilterChanged = useCallback(
    (updatedFilter: NestedFilter) => {
      setFilter(updatedFilter)
      handlePageSelect(1)
    },
    [ handlePageSelect ]
  )

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title="Ошибка загрузки товаров"
        error={error}
      />
    )
  }

  const pageInfo = data?.categories.pageInfo

  return (
    <div>
      <CategoriesFilter onFilterChanged={handleFilterChanged} />

      <CategoriesTable
        categories={data?.categories?.edges ?? []}
        loading={loading}
        tableSort={tableSort}
        onSortColumn={handleSortColumn}
        generateEditUrl={generateEditUrl}
      />

      <Pagination
        last
        first
        next
        prev
        ellipsis
        activePage={page}
        pages={pageInfo?.lastPage}
        maxButtons={10}
        onSelect={handlePageSelect}
        total={pageInfo?.total ?? 0}
      ></Pagination>
    </div>
  )
}
